import {
    Box,
    Text,
    Flex,
    Spinner,
    Center,
} from '@chakra-ui/react';
import React from 'react';
import AppDrawer from '../../components/AppDrawer';
import { Icon, IconButton } from '../../ui';
import { useInventoryImportHistory } from '../../hooks/inventory/useImportInventoryHistory';
import { INSTANT_SIDE_DRAWER_MOTION_PROPS } from '../sideDrawerContainerProps';

interface InventoryImportHistoryDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    onSelectJob: (jobId: string) => void;
    /** When set, shows a Back button in the header (e.g. return to Inventory Options). */
    onBack?: () => void;
    /** Also skip the overlay fade (sub-page swap with Inventory Options). */
    instantMotion?: boolean;
}

const InventoryImportHistoryDrawer: React.FC<InventoryImportHistoryDrawerProps> = ({
    isOpen,
    onClose,
    onSelectJob,
    onBack,
    instantMotion,
}) => {
    const { data, isLoading, isError } = useInventoryImportHistory(isOpen);
    const imports = data?.imports ?? [];

    return (
        <AppDrawer
            isOpen={isOpen}
            onClose={onClose}
            size="sm"
            title="Import History"
            titleAddon={
                onBack ? (
                    <IconButton
                        aria-label="Back"
                        icon={<Icon name="arrowLeft" size="sm" />}
                        variant="ghost"
                        colorScheme="gray"
                        size="sm"
                        ml={-2}
                        onClick={onBack}
                    />
                ) : undefined
            }
            instantMotion={instantMotion}
            contentProps={{ motionProps: INSTANT_SIDE_DRAWER_MOTION_PROPS }}
        >
            {isLoading && (
                <Center py={12}>
                    <Spinner color="#0071E3" />
                </Center>
            )}
            {isError && (
                <Text fontSize="sm" color="red.500" textAlign="center" py={8}>
                    Failed to load import history
                </Text>
            )}
            {!isLoading && !isError && imports.length === 0 && (
                <Text
                    fontSize="sm"
                    color="gray.400"
                    fontStyle="italic"
                    textAlign="center"
                    py={8}
                >
                    No imports yet
                </Text>
            )}
            {!isLoading && !isError && imports.length > 0 && (
                <Flex direction="column" gap={2}>
                    {imports.map((entry) => (
                        <Box
                            key={entry.jobId}
                            borderWidth="1px"
                            borderColor="gray.200"
                            borderRadius="lg"
                            bg="white"
                            p={3}
                            cursor="pointer"
                            _hover={{ borderColor: '#0071E3', bg: 'gray.50' }}
                            onClick={() => onSelectJob(entry.jobId)}
                        >
                            <Flex align="center" justify="space-between" gap={2}>
                                <Text
                                    fontSize="sm"
                                    fontWeight="semibold"
                                    color="gray.800"
                                >
                                    {entry.createdCount} item
                                    {entry.createdCount === 1 ? '' : 's'} imported
                                </Text>
                                <Text fontSize="xs" color="gray.500" flexShrink={0}>
                                    {new Date(entry.createdAt).toLocaleString()}
                                </Text>
                            </Flex>
                            <Text fontSize="xs" color="gray.500" mt={0.5} isTruncated>
                                Job {entry.jobId}
                            </Text>
                            {entry.filename ? (
                                <Text
                                    fontSize="xs"
                                    color="gray.500"
                                    mt={0.5}
                                    isTruncated
                                >
                                    {entry.filename}
                                </Text>
                            ) : null}
                        </Box>
                    ))}
                </Flex>
            )}
        </AppDrawer>
    );
};

export default InventoryImportHistoryDrawer;
